<template>
  <BlockFieldset class="project-email">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <b-row v-else>
      <b-col lg="3" md="3" sm="12">
        <legend>
          <strong>Vorlage</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('messageBoilerplateType')"
          label-for="messageBoilerplateType"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('messageBoilerplateType')"
            v-model="selectedMessageBoilerplate"
            :options="messageBoilerplates"
            @select="onSelectMessageBoilerplate"
            :allow-empty="true"
            :show-labels="false"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ $t(option.type) }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ $t(option.type) }}
            </template>
          </multiselect>
        </b-form-group>
      </b-col>

      <b-col lg="9" md="9" sm="12">
        <div v-if="selectedMessageBoilerplate">
          <legend>
            <strong>E-Mail "{{ $t(selectedMessageBoilerplate.type) }}" versenden</strong>
          </legend>

          <b-form-group
            horizontal
            :label="$t('subject')"
            label-for="subject"
            label-class="text-md-left"
            :label-cols="1"
            breakpoint="md"
          >
            <b-form-input
              v-model="selectedMessageBoilerplate.subject"
              type="text"
              :placeholder="$t('subject')"
            />
          </b-form-group>
          <b-form-group
            horizontal
            :label="$t('body')"
            label-for="body"
            label-class="text-md-left"
            :label-cols="1"
            breakpoint="md"
          >
            <ckeditor
              :editor="editor"
              v-model="selectedMessageBoilerplate.body"
              :config="editorConfig"
              :disabled="isBlockReadOnly"
            ></ckeditor>
          </b-form-group>

          <b-form-group
            horizontal
            :label="$t('attachmentSize')"
            label-class="text-md-right"
            :label-cols="1"
            breakpoint="md"
          >
            {{ $n(attachmentSize / 1000000) }} MB
          </b-form-group>

          <b-form-group
            horizontal
            label-for="button"
            label-class="text-md-left"
            :label-cols="1"
            breakpoint="md"
          >
            <ConfirmButton
              v-if="!isBlockReadOnly"
              colorVariant="bg"
              v-bind:disabled="buttonsDisabled"
              v-bind:id="project.id"
              v-on:confirm-button:confirmed="onSendProjectMail"
            >
              <template v-slot:buttonText>
                E-Mail versenden
              </template>
              <template v-slot:header>
                E-Mail "{{ $t(selectedMessageBoilerplate.type) }}" versenden
              </template>
              <template v-slot:content>
                <div>
                  E-mail wird an
                  <b>{{ recipient }}</b> versendet.
                </div>
              </template>
              <template v-slot:confirmButtonText>
                Versand bestätigen
              </template>
            </ConfirmButton>
            <div v-if="disabledReason" class="error-message ml-2">{{ disabledReason }}</div>
          </b-form-group>
        </div>
        <div v-else>
          <legend>
            <strong>E-Mail versenden</strong>
          </legend>
          <div class="danger">Bitte Vorlage wählen!</div>
        </div>
      </b-col>
    </b-row>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import dayjs from 'dayjs';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import ConfirmButton from '@/components/ConfirmButton';
import { genderSalutation } from '@/helpers/text';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { PROJECT_EMAIL_FIELDS } from './projectEmailFragment';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/** Field of the block on `ProjectBlocks`. */
const EMAIL_BLOCK_FIELD = 'email';

/** Templates sent to the registration address of the power company instead of the customer. */
const REGISTRATION_TEMPLATE_TYPES = ['NET_REGISTRATION', 'OPERATION_REGISTRATION'];

/** Templates a client sees with the switch off. */
const CLIENT_TEMPLATE_TYPES = [
  ...REGISTRATION_TEMPLATE_TYPES,
  'APPOINTMENT_CONFIRMATION',
  'APPOINTMENT_CONFIRMATION_MEASUREMENT',
  'APPOINTMENT_CONFIRMATION_DEFICIT',
];

/** Template preselected when an appointment of the installation type is selected. */
const APPOINTMENT_TEMPLATE_TYPE_BY_INSTALLATION_TYPE = {
  UV: 'APPOINTMENT_CONFIRMATION',
  DIRECT: 'APPOINTMENT_CONFIRMATION',
  ELECTRIC_METER_CABINET: 'APPOINTMENT_CONFIRMATION',
  REWORKING: 'APPOINTMENT_CONFIRMATION',
  MEASUREMENT: 'APPOINTMENT_CONFIRMATION_MEASUREMENT',
  DEFICIT: 'APPOINTMENT_CONFIRMATION_DEFICIT',
};

/** The appointment confirmation names a time window of two hours from the start. */
const APPOINTMENT_TIME_WINDOW_HOURS = 2;

/**
 * Block project-email ("E-Mail", see feature page projekt-kommunikation): send a project mail
 * from a template. `project` is the block data (`Project.blocks.email`): the fields the mail
 * reads plus the templates. The templates are edited in place before sending. On mounted and on
 * an appointment change the block reloads itself (fresh templates); every template selection
 * reloads the fields the mail reads together with the attachment size, so placeholders and
 * recipient use values saved by other blocks since the page was loaded. Sending uses
 * `sendProjectMail`. Switch off, a client sees only the registration and appointment templates;
 * switch on, only the block level counts.
 */
export default {
  name: 'ProjectEmail',
  components: { BlockFieldset, Multiselect, ckeditor: CKEditor.component, ConfirmButton },
  mixins: [AccessBlockMixin],
  props: {
    project: { type: Object, required: true },
    selectedAppointment: { type: Object },
  },
  data() {
    return {
      editor: InlineEditor,
      editorConfig: {},
      selectedMessageBoilerplate: null,
      buttonsDisabled: true,
      recipient: '',
      disabledReason: null,
      attachmentSize: 0,
    };
  },
  computed: {
    ...mapGetters('auth', ['isClient', 'isOrganizationRoleNavigationEnabled']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    isClientRuleApplied() {
      return !this.isOrganizationRoleNavigationEnabled && this.isClient;
    },
    /**
     * Filter out some message types for clients and sort alphabetically
     */
    messageBoilerplates() {
      const messageBoilerplates = this.project.messageBoilerplates ?? [];

      const filteredMessageBoilerplates = this.isClientRuleApplied
        ? messageBoilerplates.filter((boilerplate) =>
            CLIENT_TEMPLATE_TYPES.includes(boilerplate.type)
          )
        : messageBoilerplates;

      return [...filteredMessageBoilerplates].sort((a, b) => {
        const nameA = this.$t(a.type.toUpperCase());
        const nameB = this.$t(b.type.toUpperCase());
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
    },
  },
  methods: {
    ...mapActions([
      'fetchProjectBlockByNumber',
      'fetchProjectEmailAttachmentSize',
      'sendProjectMail',
    ]),
    messageBoilerplateOfType(type) {
      return this.messageBoilerplates.find((boilerplate) => boilerplate.type === type) ?? null;
    },
    /** Reloads the block with fresh templates; the new block data arrives as prop. */
    async reloadBlock() {
      await this.fetchProjectBlockByNumber({
        projectNumber: this.project.number,
        blockField: EMAIL_BLOCK_FIELD,
        fragment: PROJECT_EMAIL_FIELDS,
      });
      await this.$nextTick();
    },
    onSelectMessageBoilerplate() {
      this.$nextTick(() => {
        this.update();
      });
    },
    async onSendProjectMail() {
      await this.sendProjectMail({
        projectId: this.project.id,
        message: this.selectedMessageBoilerplate,
      });
    },
    isRegistrationTemplate() {
      return REGISTRATION_TEMPLATE_TYPES.includes(this.selectedMessageBoilerplate.type);
    },
    setRecipient() {
      if (!this.isRegistrationTemplate()) {
        this.recipient = this.project.customer.email;
        return;
      }
      this.recipient = this.project.powerCompany
        ? this.project.powerCompany.registrationEmail
        : 'Kein EVU ausgewählt!';
    },
    projectMailPrerequisitesCheck() {
      this.buttonsDisabled = false;
      this.disabledReason = null;
      if (
        this.selectedMessageBoilerplate.type === 'NET_REGISTRATION' &&
        this.project.netRegistrationRequirementsFullfilledState === 'NO'
      ) {
        this.disabledReason = 'Voraussetzungen Netzanmeldung nicht erfüllt!';
      }
      if (
        this.selectedMessageBoilerplate.type === 'OPERATION_REGISTRATION' &&
        this.project.operationRegistrationRequirementsFullfilledState === 'NO'
      ) {
        this.disabledReason = 'Voraussetzungen Inbetriebnahmemeldung nicht erfüllt!';
      }
    },
    parseEmailTemplate() {
      const { customer } = this.project;
      const template = this.selectedMessageBoilerplate;
      const lastname = customer.salutation === 'COMPANY' ? '' : customer.lastname;
      template.body = template.body
        .replace(/{{salutation}}/, genderSalutation(customer.salutation))
        .replace(/{{lastname}}/, lastname);
      template.subject = template.subject
        .replace(/{{lastname}}/, customer.lastname)
        .replace(/{{firstname}}/, customer.firstname)
        .replace(/{{zip}}/, customer.zip)
        .replace(/{{city}}/, customer.city);
      if (this.selectedAppointment) {
        const { installationAt } = this.selectedAppointment;
        template.body = template.body
          .replace(/{{appointment_date}}/, this.$date(installationAt))
          .replace(/{{appointment_start_time}}/, this.$time(installationAt))
          .replace(
            /{{appointment_offset_time}}/,
            this.$time(dayjs(installationAt).add(APPOINTMENT_TIME_WINDOW_HOURS, 'hour'))
          );
      }
    },
    /**
     * Reloads the fields the mail reads with the attachment size of the selected template. On a
     * failed reload the loaded values stay.
     */
    async reloadExcerptAndAttachmentSize() {
      const attachmentSize = await this.fetchProjectEmailAttachmentSize({
        projectNumber: this.project.number,
        messageBoilerplateType: this.selectedMessageBoilerplate.type,
        messageAttachmentType: this.selectedMessageBoilerplate.attachmentType,
      });
      if (attachmentSize !== null) {
        this.attachmentSize = attachmentSize;
      }
      await this.$nextTick();
    },
    async update() {
      if (!this.selectedMessageBoilerplate) {
        return;
      }
      await this.reloadExcerptAndAttachmentSize();
      this.parseEmailTemplate();
      this.setRecipient();
      this.projectMailPrerequisitesCheck();
    },
  },

  async mounted() {
    if (this.isNewProject) {
      return;
    }
    await this.reloadBlock();
    this.selectedMessageBoilerplate = this.messageBoilerplates[0] ?? null;
    await this.update();
  },
  watch: {
    /**
     * preselect APPOINTMENT_CONFIRMATION boilerplate when confirm_appointment button is pressed
     */
    selectedAppointment: {
      handler: async function(newSelectedAppointment) {
        if (this.isNewProject) {
          return;
        }
        const selectedType =
          APPOINTMENT_TEMPLATE_TYPE_BY_INSTALLATION_TYPE[newSelectedAppointment.installationType] ??
          this.selectedMessageBoilerplate?.type;
        await this.reloadBlock();
        this.selectedMessageBoilerplate = this.messageBoilerplateOfType(selectedType);
        await this.update();
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
.error-message {
  color: red;
  display: inline-block;
}
</style>
